import React, { useState, useCallback } from "react";
import { debounce, isEmpty, get } from "lodash";
import { Combobox } from "@headlessui/react";
import classNames from "classnames";
import useGetUsers from "../InviteUsers/services/useGetUsers";
import Tag from "./Tag";
import { isValidEmail } from "../../utils";
import { useTranslation } from "react-i18next";

const Option = ({ user, onlyEmail }) => {
  return (
    <Combobox.Option
      className={classNames(
        "cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 px-3"
      )}
      value={user}
    >
      <div className="flex items-center">
        <span className="flex items-center">
          <svg
            className="hidden ui-selected:inline-block"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1C1.89688 1 1 1.89688 1 3V13C1 14.1031 1.89688 15 3 15H13C14.1031 15 15 14.1031 15 13V3C15 1.89688 14.1031 1 13 1H3ZM11.5313 6.53125L7.53125 10.5312C7.2375 10.825 6.7625 10.825 6.47188 10.5312L4.47188 8.53125C4.17813 8.2375 4.17813 7.7625 4.47188 7.47188C4.76563 7.18125 5.24063 7.17813 5.53125 7.47188L7 8.94063L10.4688 5.46875C10.7625 5.175 11.2375 5.175 11.5281 5.46875C11.8188 5.7625 11.8219 6.2375 11.5281 6.52813L11.5313 6.53125Z"
              fill="#00A9A7"
            />
            <path d="M11.5313 6.53125L7.53125 10.5312C7.2375 10.825 6.7625 10.825 6.47188 10.5312L4.47188 8.53125C4.17813 8.2375 4.17813 7.7625 4.47188 7.47188C4.76563 7.18125 5.24063 7.17813 5.53125 7.47188L7 8.94063L10.4688 5.46875C10.7625 5.175 11.2375 5.175 11.5281 5.46875C11.8188 5.7625 11.8219 6.2375 11.5281 6.52813L11.5313 6.53125Z" />
          </svg>
          <svg
            className="inline-block ui-selected:hidden"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 3C1 1.89531 1.89531 1 3 1H13C14.1031 1 15 1.89531 15 3V13C15 14.1031 14.1031 15 13 15H3C1.89531 15 1 14.1031 1 13V3ZM2.5 3V13C2.5 13.275 2.72375 13.5 3 13.5H13C13.275 13.5 13.5 13.275 13.5 13V3C13.5 2.72375 13.275 2.5 13 2.5H3C2.72375 2.5 2.5 2.72375 2.5 3Z"
              fill="#9EA3A8"
            />
          </svg>
        </span>
        {onlyEmail ? (
          <div className="ml-3">{user.email}</div>
        ) : (
          <div className="ml-3 text-md">
            {user.name}
            <div className="text-sm">{user.email}</div>
          </div>
        )}
      </div>
    </Combobox.Option>
  );
};

const SelectUser = ({ value, onChange }) => {
  const [text, setText] = useState("");
  const { t } = useTranslation();

  const { data: usersObj, isLoading: usersLoading } = useGetUsers(text);

  const { users = [] } = usersObj || {};

  let onChangeInput = useCallback(
    debounce((_text) => setText(_text), 500),
    []
  );

  return (
    <>
      {!isEmpty(value) && (
        <div className="flex w-full flex-wrap gap-3">
          {value.map((item) => (
            <Tag
              key={item.email}
              label={item.name}
              onClick={() => {
                const updatedValues = value.filter((i) => i.email !== item.email);
                setTimeout(() => onChange(updatedValues), 0);
              }}
              isSelected={true}
              labelMappings={{}}
            />
          ))}
        </div>
      )}
      <Combobox
        value={value}
        onChange={onChange}
        multiple
        as={"div"}
        className="relative"
      >
        <div className="flex items-center border border-gray-600 rounded-6 pr-2 ui-open:rounded-b-none">
          <Combobox.Input
            placeholder={t("report_filter_placeholder")}
            className="relative border-none leading-5 sm:text-[12px] truncate rounded-6 bg-transparent flex-1 flex items-center px-5 py-3 pr-7"
            onChange={(event) => onChangeInput(get(event, ["target", "value"], ""))}
          />
          <svg
            className="text-teal-500 dark:text-gray-400 w-6 h-6 cursor-pointer"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4 10C4 6.68629 6.68629 4 10 4C13.3137 4 16 6.68629 16 10C16 13.3137 13.3137 16 10 16C6.68629 16 4 13.3137 4 10ZM10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C11.8487 18 13.551 17.3729 14.9056 16.3199L20.2929 21.7071C20.6834 22.0976 21.3166 22.0976 21.7071 21.7071C22.0976 21.3166 22.0976 20.6834 21.7071 20.2929L16.3199 14.9056C17.3729 13.551 18 11.8487 18 10C18 5.58172 14.4183 2 10 2Z"
              className={classNames("fill-teal-500 dark:fill-gray-100")}
            />
          </svg>
        </div>
        <Combobox.Options className="absolute top-full z-10 w-full py-3 space-y-2 border border-t-0 border-gray-600 bg-gray-100 dark:bg-gray-800 rounded-6 rounded-t-none overflow-hidden max-h-[200px] overflow-y-auto scrollbar-light">
          {usersLoading ? (
            <div className="p-3 text-center">{t("loading_key")}...</div>
          ) : isValidEmail(text) && isEmpty(users) ? (
            <Option
              user={{
                name: text,
                email: text,
              }}
              onlyEmail
            />
          ) : (
            users.map((user) => <Option user={user} key={user.email} />)
          )}
        </Combobox.Options>
      </Combobox>
    </>
  );
};

export default SelectUser;
